import type { CurrencyAmount } from '@/services/lineItems/lineItemsApi'
import { getLocale } from './locale'

/**
 * Formats a monetary amount for display. CPM prices and fees run to fractions
 * of a cent, so up to four decimals are kept — `maximumFractionDigits` can be
 * lowered for amounts that are whole money, like budgets.
 */
export function formatMoney(
  amount: number,
  currency?: string,
  maximumFractionDigits = 4
): string {
  return new Intl.NumberFormat(getLocale(), {
    style: 'currency',
    currency: currency || 'USD',
    // Intl rejects a maximum below the minimum, so asking for whole money
    // (0 decimals) has to lower the floor with it.
    minimumFractionDigits: Math.min(2, maximumFractionDigits),
    maximumFractionDigits,
  }).format(amount)
}

/** The same, for the `{amount, currency}` pairs the API returns. */
export function formatCurrencyAmount(
  value: CurrencyAmount | undefined,
  maximumFractionDigits?: number
): string {
  if (!value || typeof value.amount !== 'number') return '—'
  return formatMoney(value.amount, value.currency, maximumFractionDigits)
}

/**
 * An amount that spans a range — "$0.20 – $0.50" — collapsing to a single
 * amount when both ends match.
 */
export function formatMoneyRange(
  amount: number,
  maxAmount: number | undefined,
  currency?: string,
  maximumFractionDigits?: number
): string {
  const low = formatMoney(amount, currency, maximumFractionDigits)
  if (maxAmount == null || maxAmount === amount) return low
  return `${low} – ${formatMoney(maxAmount, currency, maximumFractionDigits)}`
}
